import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { MessageCircle, X, Send, Loader2, Sparkles } from "lucide-react";
import { useI18n } from "@/lib/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function genSessionId() {
  if (typeof window === "undefined") return "sess_" + Date.now();
  const stored = localStorage.getItem("mp_chat_session");
  if (stored) return stored;
  const newId =
    "sess_" +
    Math.random().toString(36).slice(2, 10) +
    "_" +
    Date.now().toString(36);
  localStorage.setItem("mp_chat_session", newId);
  return newId;
}

export default function ChatbotWidget() {
  const { t, lang } = useI18n();
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: t.chatbot.greeting },
  ]);
  const sessionRef = useRef(genSessionId());
  const scrollRef = useRef(null);

  // Update greeting on language change (only initial assistant message)
  useEffect(() => {
    setMessages((curr) => {
      if (curr.length === 1 && curr[0].role === "assistant") {
        return [{ role: "assistant", content: t.chatbot.greeting }];
      }
      return curr;
    });
  }, [lang, t.chatbot.greeting]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }]);
    setSending(true);
    try {
      const res = await axios.post(`${API}/chat`, {
        session_id: sessionRef.current,
        message: text,
        language: lang,
      });
      const reply = res.data?.reply || "…";
      setMessages((m) => [...m, { role: "assistant", content: reply }]);
    } catch (err) {
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content:
            "Entschuldigung, ich bin gerade nicht erreichbar. Bitte versuchen Sie es erneut oder kontaktieren Sie uns via WhatsApp.",
        },
      ]);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {/* Floating button */}
      <button
        data-testid="chatbot-toggle-button"
        onClick={() => setOpen((s) => !s)}
        aria-label={t.chatbot.open}
        className="fixed bottom-20 right-6 z-[60] w-14 h-14 md:w-16 md:h-16 rounded-full bg-white text-black hover:bg-zinc-200 active:scale-95 flex items-center justify-center shadow-2xl transition-all duration-300 group"
      >
        {open ? (
          <X className="w-6 h-6" />
        ) : (
          <>
            <MessageCircle className="w-7 h-7" />
            <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-[#e52620] flex items-center justify-center">
              <Sparkles className="w-3 h-3 text-white" />
            </span>
          </>
        )}
      </button>

      {/* Panel */}
      {open && (
        <div
          data-testid="chatbot-panel"
          className="fixed bottom-40 right-4 md:right-6 z-[60] w-[calc(100%-2rem)] sm:w-96 max-h-[70vh] bg-[#0b0b0b] border border-white/10 rounded-2xl shadow-2xl flex flex-col overflow-hidden animate-fade-up"
        >
          {/* Header */}
          <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between bg-gradient-to-b from-[#141414] to-[#0b0b0b]">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-md bg-[#e52620] flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-white" />
              </div>
              <div>
                <div className="text-white font-bold tracking-tight">
                  {t.chatbot.title}
                </div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">
                  {t.chatbot.subtitle}
                </div>
              </div>
            </div>
            <button
              data-testid="chatbot-close-button"
              onClick={() => setOpen(false)}
              className="text-zinc-400 hover:text-white p-1"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages */}
          <div
            ref={scrollRef}
            data-testid="chatbot-messages"
            className="flex-1 overflow-y-auto px-4 py-4 space-y-3"
          >
            {messages.map((m, i) => (
              <div
                key={i}
                className={`flex ${
                  m.role === "user" ? "justify-end" : "justify-start"
                }`}
              >
                <div
                  data-testid={`chatbot-message-${m.role}-${i}`}
                  className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                    m.role === "user"
                      ? "bg-[#e52620] text-white rounded-br-md"
                      : "bg-[#1a1a1a] text-zinc-100 border border-white/5 rounded-bl-md"
                  }`}
                >
                  {m.content}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="bg-[#1a1a1a] border border-white/5 rounded-2xl rounded-bl-md px-4 py-3 text-sm text-zinc-400 flex items-center gap-2">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  <span className="text-xs">…</span>
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <form
            onSubmit={send}
            className="border-t border-white/10 p-3 flex items-center gap-2 bg-[#0b0b0b]"
          >
            <input
              data-testid="chatbot-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={t.chatbot.placeholder}
              className="flex-1 bg-[#1a1a1a] border border-white/10 focus:border-[#e52620] outline-none rounded-md px-4 py-2.5 text-sm text-white placeholder-zinc-600 transition-colors"
            />
            <button
              type="submit"
              data-testid="chatbot-send-button"
              disabled={sending || !input.trim()}
              className="w-10 h-10 flex-shrink-0 rounded-md bg-[#e52620] hover:bg-[#b51e18] disabled:opacity-50 text-white flex items-center justify-center transition-colors active:scale-95"
              aria-label={t.chatbot.send}
            >
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
